import { Injectable } from '@angular/core';
import {
  FinancialConnectionsSession,
  loadStripe,
  Stripe,
  StripeElements,
  StripeElementsOptionsClientSecret,
  StripeElementsOptionsMode
} from '@stripe/stripe-js';
import { ServiceResultModel } from '../../models/service/service-result.model';
import { environment } from '../../../../environments/environment';
import { ServiceUtilities } from '../../utilities/service.utilities';

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  private stripe: Stripe | null = null;
  private isInitialized: boolean = false;

  constructor() {}

  public async initializeStripe(): Promise<ServiceResultModel<Stripe>> {
    let submissionResult: ServiceResultModel<Stripe> = {};

    try {
      if (this.isInitialized && this.stripe) {
        submissionResult.data = this.stripe;
        return { ...submissionResult, success: true, message: 'Stripe already initialized.' };
      }

      const publishableKey = environment.stripePublishableKey;

      if (!publishableKey) {
        return { 
          ...submissionResult, 
          success: false, 
          message: 'Stripe publishable key is not configured.' 
        };
      }

      this.stripe = await loadStripe(publishableKey);

      if (!this.stripe) {
        return { 
          ...submissionResult, 
          success: false, 
          message: 'Failed to load Stripe. Please check your internet connection.' 
        };
      }

      this.isInitialized = true;
      submissionResult.data = this.stripe;
      return { ...submissionResult, success: true, message: 'Stripe initialized successfully.' };

    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getStripeInstance(): Promise<Stripe | null> {
    if (!this.isInitialized || !this.stripe) {
      const result = await this.initializeStripe();
      return result.data || null;
    }
    return this.stripe;
  }

  public async createElement(options?: StripeElementsOptionsClientSecret): Promise<ServiceResultModel<StripeElements>> {
    return this.initializeElements((stripe) => stripe.elements(options));
  }

  public async createDeferredElement(options: StripeElementsOptionsMode): Promise<ServiceResultModel<StripeElements>> {
    return this.initializeElements((stripe) => stripe.elements(options));
  }

  private async initializeElements(factory: (stripe: Stripe) => StripeElements): Promise<ServiceResultModel<StripeElements>> {
    let submissionResult: ServiceResultModel<StripeElements> = {};

    try {
      const stripe = await this.getStripeInstance();

      if (!stripe) {
        return {
          ...submissionResult,
          success: false,
          message: 'Stripe is not initialized.'
        };
      }

      submissionResult.data = factory(stripe);
      return { ...submissionResult, success: true, message: 'Elements created successfully.' };

    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public isStripeInitialized(): boolean {
    return this.isInitialized && this.stripe !== null;
  }

  /**
   * Opens Stripe's hosted Financial Connections modal, which owns institution search, credential
   * entry and the account picker. Nothing typed in there reaches us.
   *
   * `accounts` comes back empty when the user closes the modal without linking anything, which is not
   * an error — the caller decides what an empty collection means.
   */
  public async collectFinancialConnectionsAccounts(
    clientSecret: string
  ): Promise<ServiceResultModel<FinancialConnectionsSession.Account[]>> {
    let submissionResult: ServiceResultModel<FinancialConnectionsSession.Account[]> = {};

    try {
      const stripe = await this.getStripeInstance();

      if (!stripe) {
        return {
          ...submissionResult,
          success: false,
          message: 'Stripe is not initialized.'
        };
      }

      const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret });

      if (result.error) {
        return {
          ...submissionResult,
          success: false,
          message: result.error.message ?? 'The bank connection could not be completed.'
        };
      }

      submissionResult.data = result.financialConnectionsSession.accounts;
      return { ...submissionResult, success: true };

    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

}